import { ReactNode, useRef, useEffect, useState } from "react";

type Direction = "up" | "down" | "left" | "right" | "none";
type AnimationType = "fade" | "slide" | "scale" | "slideScale";

interface ScrollRevealProps {
  children: ReactNode;
  direction?: Direction;
  type?: AnimationType;
  delay?: number;
  duration?: number;
  className?: string;
  once?: boolean;
  amount?: number;
}

// Custom hook for intersection observer that doesn't cause forced reflow
function useInView(options: { once?: boolean; amount?: number } = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Use requestAnimationFrame to batch the state update
            requestAnimationFrame(() => {
              setIsInView(true);
            });
            if (options.once) {
              observer.unobserve(element);
            }
          } else if (!options.once) {
            requestAnimationFrame(() => {
              setIsInView(false);
            });
          }
        });
      },
      {
        threshold: options.amount ?? 0.2,
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [options.once, options.amount]);

  return { ref, isInView };
}

// CSS animation classes based on direction and type
const getAnimationClass = (direction: Direction, type: AnimationType): string => {
  const baseClass = "transition-all ease-out";
  
  switch (type) {
    case "scale":
      return `${baseClass} data-[inview=false]:opacity-0 data-[inview=false]:scale-95 data-[inview=true]:opacity-100 data-[inview=true]:scale-100`;
    case "slideScale":
    case "slide":
    case "fade":
    default:
      return baseClass;
  }
};

const getInitialStyle = (direction: Direction, type: AnimationType, isInView: boolean): React.CSSProperties => {
  if (isInView) {
    return {
      opacity: 1,
      transform: "translate(0, 0) scale(1)",
    };
  }

  const distance = 20;
  let transform = "";

  switch (direction) {
    case "up":
      transform = `translateY(${distance}px)`;
      break;
    case "down":
      transform = `translateY(-${distance}px)`;
      break;
    case "left":
      transform = `translateX(${distance}px)`;
      break;
    case "right":
      transform = `translateX(-${distance}px)`;
      break;
    case "none":
    default:
      transform = "translate(0, 0)";
  }

  if (type === "scale" || type === "slideScale") {
    transform += " scale(0.98)";
  }

  return {
    opacity: 0,
    transform,
  };
};

export function ScrollReveal({
  children,
  direction = "up",
  type = "fade",
  delay = 0,
  duration = 0.3,
  className = "",
  once = true,
  amount = 0.2,
}: ScrollRevealProps) {
  const { ref, isInView } = useInView({ once, amount });
  const style = getInitialStyle(direction, type, isInView);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        transitionProperty: "opacity, transform",
        transitionDuration: `${duration}s`,
        transitionTimingFunction: "ease-out",
        transitionDelay: `${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

// Staggered container for child animations
interface StaggerContainerProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
  once?: boolean;
}

export function StaggerContainer({
  children,
  className = "",
  staggerDelay = 0.1,
  once = true,
}: StaggerContainerProps) {
  const { ref, isInView } = useInView({ once, amount: 0.2 });

  return (
    <div
      ref={ref}
      className={className}
      data-stagger-inview={isInView}
      data-stagger-delay={staggerDelay}
      style={{
        // CSS custom property for stagger delay
        "--stagger-delay": `${staggerDelay}s`,
      } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

// Staggered child item
interface StaggerItemProps {
  children: ReactNode;
  className?: string;
  direction?: Direction;
  index?: number;
}

export function StaggerItem({
  children,
  className = "",
  direction = "up",
  index = 0,
}: StaggerItemProps) {
  const distance = 15;
  const parentRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [staggerDelay, setStaggerDelay] = useState(0.1);

  useEffect(() => {
    const element = parentRef.current;
    if (!element) return;

    // Find parent with stagger data
    const parent = element.closest("[data-stagger-inview]");
    if (parent) {
      const checkParent = () => {
        const inView = parent.getAttribute("data-stagger-inview") === "true";
        const delay = parseFloat(parent.getAttribute("data-stagger-delay") || "0.1");
        setIsInView(inView);
        setStaggerDelay(delay);
      };

      // Initial check
      checkParent();

      // Observe parent for attribute changes
      const observer = new MutationObserver(checkParent);
      observer.observe(parent, { attributes: true, attributeFilter: ["data-stagger-inview"] });

      return () => observer.disconnect();
    }
  }, []);

  // Auto-detect index from siblings if not provided
  useEffect(() => {
    if (parentRef.current && index === 0) {
      const parent = parentRef.current.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children);
        const idx = siblings.indexOf(parentRef.current);
        if (idx > 0) {
          // Update would cause re-render, so we use CSS variable instead
          parentRef.current.style.setProperty("--item-index", String(idx));
        }
      }
    }
  }, [index]);

  const getTransform = (): string => {
    if (isInView) return "translate(0, 0)";
    
    switch (direction) {
      case "up":
        return `translateY(${distance}px)`;
      case "down":
        return `translateY(-${distance}px)`;
      case "left":
        return `translateX(${distance}px)`;
      case "right":
        return `translateX(-${distance}px)`;
      default:
        return "translate(0, 0)";
    }
  };

  return (
    <div
      ref={parentRef}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: getTransform(),
        transitionProperty: "opacity, transform",
        transitionDuration: "0.25s",
        transitionTimingFunction: "ease-out",
        transitionDelay: `${index * staggerDelay}s`,
      }}
    >
      {children}
    </div>
  );
}
